import React, {Component} from 'react';
import ReactDOM from "react-dom";
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import i18n from 'i18next';
import { getBBDDLexon, getContactsLexon } from '../../../../services/contacts';

import { getContactFilterList } from '../../../../calendar/api/contacts-api';
import IconRemove from '../../../../assets/icons/close-round.svg?react';
import {
  filterContactsBySearch,
  calculateSearchRelevance,
  calculateStringRelevance,
  removeDuplicateContacts
} from '../../../../utils/contacts-utils';
import BannerStatusMessage from '../../banner-status-message/BannerStatusMessage';

// Module-level cache shared across all AttendeeAddress instances
let _sharedLexonContactsCache = null;
let _sharedLexonContactsPromise = null;
let _sharedLexonContactsCacheKey = null;

class AttendeeAddress extends Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.containerRef = React.createRef();
    this.chipsContainerRef = React.createRef();
    this.measureChipRefs = [];
    this.resizeObserver = null;
    this.resizeRafId = null;
    this.handleOnSuggestionChange = this.onSuggestionChange.bind(this);
    this.handleOnSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.handleOnSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.handleOnSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.handleOnHeaderKeyDown = this.onHeaderKeyDown.bind(this);
    this.handleOnHeaderBlur = this.onHeaderBlur.bind(this);
    this.state = {
      value: '',
      suggestions: [],
      nameEmailMap: {},
      lexonContactsReady: false,
      visibleChipCount: Array.isArray(props.addresses) ? props.addresses.length : 0,
      isCollapsed: true
    };
    this.callIndex = 0;
    this._isMounted = false;
    this.contactSourceMap = new Map();
  }

  validateEmail = (addresses) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return addresses.map(email => emailRegex.test(email.trim()));
  }

  componentDidMount() {
    this._isMounted = true;
    // Preload Lexon contacts on mount
    this.loadLexonContactsOnce();
    this.initializeResizeObserver();
    this.scheduleVisibleChipCountUpdate();
  }

  componentDidUpdate(prevProps, prevState) {
    // When Lexon contacts cache is loaded, refresh suggestions if there's a search value
    if (!prevState.lexonContactsReady && this.state.lexonContactsReady) {
      if (this.state.value.length > 0) {
        this.onSuggestionsFetchRequested({ value: this.state.value });
      }
    }
    if (
      prevProps.addresses !== this.props.addresses ||
      prevState.value !== this.state.value
    ) {
      this.scheduleVisibleChipCountUpdate();
    }

    if (!this.resizeObserver && this.chipsContainerRef.current) {
      this.initializeResizeObserver();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.callIndex = -1;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeRafId) {
      window.cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }
    // Clear shared cache when component unmounts
    _sharedLexonContactsCache = null;
    _sharedLexonContactsPromise = null;
    _sharedLexonContactsCacheKey = null;
  }

  initializeResizeObserver = () => {
    if (typeof ResizeObserver === "undefined" || !this.chipsContainerRef.current) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleVisibleChipCountUpdate();
    });

    this.resizeObserver.observe(this.chipsContainerRef.current);
  };

  scheduleVisibleChipCountUpdate = () => {
    if (this.resizeRafId) {
      window.cancelAnimationFrame(this.resizeRafId);
    }

    this.resizeRafId = window.requestAnimationFrame(() => {
      this.resizeRafId = null;
      this.updateVisibleChipCount();
    });
  };

  updateVisibleChipCount = () => {
    const addresses = this.props.addresses || [];

    if (!addresses.length) {
      if (this.state.visibleChipCount !== 0) {
        this.setState({ visibleChipCount: 0 });
      }
      return;
    }

    const chipsContainer = this.chipsContainerRef.current;
    if (!chipsContainer) {
      return;
    }

    const containerWidth = chipsContainer.getBoundingClientRect().width;
    if (!containerWidth) {
      return;
    }

    const MIN_INPUT_WIDTH = 180;
    const ACTIONS_WIDTH = 32;
    const HIDDEN_COUNTER_WIDTH = 40;

    const chipWidths = addresses.map((_, index) => {
      const chipNode = this.measureChipRefs[index];
      return chipNode ? Math.ceil(chipNode.getBoundingClientRect().width) : 0;
    });

    const getVisibleCount = (maxWidth) => {
      let usedWidth = 0;
      let count = 0;

      for (let index = 0; index < chipWidths.length; index += 1) {
        const chipWidth = chipWidths[index];
        if (!chipWidth) {
          break;
        }

        if (usedWidth + chipWidth > maxWidth) {
          break;
        }

        usedWidth += chipWidth;
        count += 1;
      }

      return count;
    };

    const availableChipWidth = Math.max(0, containerWidth - MIN_INPUT_WIDTH - ACTIONS_WIDTH);
    let nextVisibleChipCount = getVisibleCount(availableChipWidth);

    if (nextVisibleChipCount < addresses.length) {
      nextVisibleChipCount = getVisibleCount(
        Math.max(0, availableChipWidth - HIDDEN_COUNTER_WIDTH)
      );
    }

    if (nextVisibleChipCount === 0 && addresses.length > 0) {
      nextVisibleChipCount = 1;
    }

    if (this.state.visibleChipCount !== nextVisibleChipCount) {
      this.setState({ visibleChipCount: nextVisibleChipCount });
    }
  };

  handleToggleCollapsed = (event) => {
    event.stopPropagation();

    const addresses = this.props.addresses || [];
    const collapsedHiddenCount = Math.max(0, addresses.length - this.state.visibleChipCount);

    if (this.state.isCollapsed && collapsedHiddenCount === 0) {
      return;
    }

    this.setState(
      (prevState) => ({ isCollapsed: !prevState.isCollapsed }),
      this.scheduleVisibleChipCountUpdate
    );
  };

  extractEmailFromSuggestion = (suggestion) => {
    const cleanSuggestion = suggestion.replace(/\s*LexOn\s*$/, '');
    // Try angle brackets first: "Name" <email>
    let match = cleanSuggestion.match(/(.*)<(.*)>/);
    if (match) {
      return match[2].trim();
    }
    // Try parentheses: Name (email)
    match = cleanSuggestion.match(/(.*)\(([^)]+@[^)]+)\)/);
    if (match) {
      return match[2].trim();
    }
    return cleanSuggestion.trim();
  };

  // Load Lexon contacts from API once and cache them (shared across all instances)
  async loadLexonContactsOnce() {
    if (!this.props.lexon || !this.props.lexon.userId || !this.props.lexon.idClientLef) {
      return;
    }

    const env = window.currentUser?.env || 'PRO';
    const cacheKey = `${this.props.lexon.userId}_${this.props.lexon.idClientLef}_${env}`;

    // If cache exists and is for the same user, use it
    if (_sharedLexonContactsCache !== null && _sharedLexonContactsCacheKey === cacheKey) {
      if (this._isMounted && !this.state.lexonContactsReady) {
        this.setState({ lexonContactsReady: true });
      }
      return;
    }

    // If already loading, wait for the existing promise
    if (_sharedLexonContactsPromise !== null && _sharedLexonContactsCacheKey === cacheKey) {
      try {
        await _sharedLexonContactsPromise;
        if (this._isMounted && !this.state.lexonContactsReady) {
          this.setState({ lexonContactsReady: true });
        }
      } catch (err) {
        console.log('Error waiting for Lexon contacts:', err);
      }
      return;
    }

    // Start loading - set cache key immediately to prevent race conditions
    _sharedLexonContactsCacheKey = cacheKey;
    
    const loadContacts = async () => {
      try {
        const lexDataBases = await getBBDDLexon(this.props.lexon.userId, this.props.lexon.idClientLef, env, this.props.lexonToken);

        if (lexDataBases && lexDataBases.data !== null) {
          const lexUserId = lexDataBases.data.idUser;
          const contactsLexon = [];

          const contactPromises = lexDataBases.data.companies.map(company =>
            getContactsLexon(lexUserId, company.idCompany, company.bbdd, env, this.props.lexonToken)
              .then(contacts => {
                if (contacts && contacts.data && contacts.data.data) {
                  contacts.data.data.forEach(contact => {
                    if (contact.email && contact.email !== null) {
                      contact.id = `${contact.id}_${contact.idType}_${contact.entityType}_${company.bbdd}`;
                      contactsLexon.push(contact);
                    }
                  });
                }
              })
              .catch(err => {
                console.log('Error fetching contacts for company:', company.idCompany, err);
                return [];
              })
          );

          await Promise.all(contactPromises);
          _sharedLexonContactsCache = contactsLexon;
        } else {
          _sharedLexonContactsCache = [];
        }
      } catch (err) {
        console.log('Error loading Lexon contacts:', err);
        _sharedLexonContactsCache = [];
      } finally {
        _sharedLexonContactsPromise = null;
      }
    };

    _sharedLexonContactsPromise = loadContacts();
    await _sharedLexonContactsPromise;
    
    if (this._isMounted && !this.state.lexonContactsReady) {
      this.setState({ lexonContactsReady: true });
    }
  }

  // Get filtered Lexon contacts from shared cache
  getDataLexon(searchValue = '') {
    // If not cached yet, trigger load and return empty
    if (_sharedLexonContactsCache === null) {
      this.loadLexonContactsOnce();
      return [];
    }

    // Filter cached contacts locally
    const filteredContacts = filterContactsBySearch(_sharedLexonContactsCache, searchValue);

    const contactObjects = filteredContacts.map(contact => ({
      displayName: contact.name && contact.email ?
        `${contact.name} <${contact.email}>` :
        contact.email || contact.name,
      email: contact.email,
      name: contact.name,
      isLexon: true,
      originalContact: contact,
      searchRelevance: calculateSearchRelevance(contact, searchValue)
    }));

    return contactObjects;
  }

  prioritizeSuggestions = (externalContacts, lexonContacts, searchValue) => {
    const externalContactObjects = (externalContacts || []).map(contact => ({
      displayName: contact,
      isLexon: false,
      searchRelevance: calculateStringRelevance(contact, searchValue)
    }));

    const allContacts = [...externalContactObjects, ...(lexonContacts || [])];
    
    // Remove duplicates by email+name, prioritizing LexON contacts
    const uniqueContacts = removeDuplicateContacts(allContacts);
    
    const sortedContacts = uniqueContacts.sort((a, b) => {
      if (a.searchRelevance !== b.searchRelevance) {
        return b.searchRelevance - a.searchRelevance;
      }
      if (a.isLexon !== b.isLexon) {
        return a.isLexon ? -1 : 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });

    this.contactSourceMap = new Map();
    sortedContacts.forEach(contact => {
      this.contactSourceMap.set(contact.displayName, contact.isLexon);
    });

    const suggestions = sortedContacts.map(contact => contact.displayName);

    return suggestions;
  };

  handleAddressRemove = (id, address) => {
    const { onAddressRemove } = this.props;

    this.setState(prevState => {
      const newNameEmailMap = { ...prevState.nameEmailMap };
      delete newNameEmailMap[address];
      return { nameEmailMap: newNameEmailMap };
    });

    onAddressRemove(id, address);
  };

  render() {
    const {
      id,
      addresses,
      portalContainer,
      forwardedRef,
      isInvalidEmail,
      setInvalidEmail,
    } = this.props;

    const { suggestions, value, visibleChipCount, isCollapsed } = this.state;

    // Validar cada dirección
    const validArray = this.validateEmail(addresses);
    const normalizedVisibleChipCount = Math.min(addresses.length, Math.max(visibleChipCount, 0));
    const collapsedHiddenCount = Math.max(0, addresses.length - normalizedVisibleChipCount);
    const canToggleCollapse = collapsedHiddenCount > 0 || !isCollapsed;
    const visibleAddresses = isCollapsed
      ? addresses.slice(0, normalizedVisibleChipCount)
      : addresses;
    const hiddenCount = isCollapsed ? collapsedHiddenCount : 0;

    const content = (
      <div
        ref={(node) => {
          this.containerRef.current = node;
          if (forwardedRef) {
            if (typeof forwardedRef === 'function') {
              forwardedRef(node);
            } else {
              forwardedRef.current = node;
            }
          }
        }}
        className="mb-4"
        onClick={() => this.fieldClick()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => this.onDrop(e, id)}
      >
        <label className="label-field-text text-color-grey-800 inter-regular-16">
          {i18n.t("schedule.attendees")}
        </label>
        <div
          className={`input-group containeratt${isCollapsed ? "" : " containeratt--expanded"}`}
          ref={this.chipsContainerRef}
        >
          <div className="attendee-chip-input-area">
            {visibleAddresses.map((address, index) => (
              <div
                key={index}
                className={`mdc-chip`}
                style={{ background: validArray[index] ? 'var(--ghost-white)' : 'var(--whisper-blue)' }}
                draggable={true}
                onDragStart={(event) =>
                  AttendeeAddress.onAddressDragStart(event, id, address)
                }
              >
                <div className={"mdc-chip__text"}>{address}</div>
                <IconRemove
                  className="mdc-chip-icon lf-icon lf-icon-close-round"
                  onClick={() => this.handleAddressRemove(id, address)}
                />
              </div>
            ))}
            <Autosuggest
              suggestions={suggestions}
              ref={this.inputRef}
              inputProps={{
                id: id,
                type: "text",
                value: value,
                onChange: this.handleOnSuggestionChange,
                onKeyDown: this.handleOnHeaderKeyDown,
                onBlur: this.handleOnHeaderBlur,
                placeholder: addresses.length === 0 && i18n.t('schedule.attendees_placeholder'),
              }}
              getSuggestionValue={AttendeeAddress.getSuggestionValue}
              renderSuggestion={this.renderSuggestion}
              onSuggestionsFetchRequested={this.handleOnSuggestionsFetchRequested}
              onSuggestionsClearRequested={this.handleOnSuggestionsClearRequested}
              onSuggestionSelected={this.handleOnSuggestionSelected}
              shouldRenderSuggestions={(val) => val.length >= 3}
              theme={{
                container: `header-address-container`,
                suggestionsContainer: `header-address-suggestions-container`,
                suggestionsContainerOpen: `header-address-suggestions-containerOpen`,
                suggestionsList: `header-address-suggestions-list`,
                suggestion: "header-address-suggestion",
                suggestionHighlighted: "header-address-suggestion-highlighted",
              }}
            />
          </div>

          <div
            className={`attendee-input-actions${canToggleCollapse ? " attendee-input-actions--interactive" : ""}${!isCollapsed ? " attendee-input-actions--expanded" : ""}`}
            onClick={this.handleToggleCollapsed}
          >
            {hiddenCount > 0 && <div className="attendee-hidden-count">{`+${hiddenCount}`}</div>}
            <div className="attendee-actions-divider" />
            <i className={`lf-icon ${isCollapsed ? "lf-icon-angle-down" : "lf-icon-angle-up"} attendee-toggle-icon`} />
          </div>
        </div>

        <div className="attendee-chip-measurements" aria-hidden="true">
          {addresses.map((address, index) => (
            <div
              key={`measure-${index}`}
              className="mdc-chip mdc-chip--measure"
              ref={(node) => {
                this.measureChipRefs[index] = node;
              }}
            >
              <div className="mdc-chip__text">{address}</div>
              <IconRemove className="mdc-chip-icon lf-icon lf-icon-close-round" />
            </div>
          ))}
        </div>
        <BannerStatusMessage 
          showBanner={isInvalidEmail} 
          type="error" 
          message={i18n.t("general.invalid_email")} 
          onClose={() => setInvalidEmail(false)} 
        />
        <style jsx>{`
          .header-address-suggestions-container {
            position: absolute !important;
            z-index: 1000;
            background-color: white;
            width: 100% !important;
            left: 0px !important;
            top: 100% !important;
            transform: none !important;
            margin-left: 0 !important;
            margin-top: 0 !important;
          }

          .mb-4 {
            position: relative;
          }

          .header-address-container {
            flex: 1 1 1px;
            position: static !important;
            width: 1px;
          }

          .containeratt {
            margin-bottom: 2px;
            border: 1px solid var(--grey-400);
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 8px 6px 6px;
            column-gap: 8px;
            min-height: 38px;
            height: auto !important;
            box-sizing: border-box;
            position: relative;
            background-color: var(--white, #fff);
          }

          .containeratt--expanded {
            align-items: flex-start;
          }

          .attendee-chip-input-area {
            flex: 1;
            display: flex;
            align-items: center;
            align-content: flex-start;
            flex-wrap: wrap;
            gap: 6px;
            min-width: 0;
          }

          .attendee-input-actions {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
            align-self: center;
            position: relative;
          }

          .attendee-input-actions--expanded {
            align-self: stretch;
            min-width: 24px;
            justify-content: center;
          }

          .attendee-input-actions--interactive {
            cursor: pointer;
            user-select: none;
          }

          .attendee-input-actions--interactive:hover .attendee-toggle-icon {
            color: var(--blue-lefebvre);
          }

          .attendee-actions-divider {
            width: 1px;
            height: 24px;
            background-color: var(--grey-400);
          }

          .attendee-input-actions--expanded .attendee-actions-divider {
            height: 100%;
          }

          .attendee-hidden-count {
            min-width: 26px;
            height: 22px;
            padding: 0 8px;
            border-radius: 11px;
            border: 1px solid var(--grey-300, #d9d9d9);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: var(--grey-800);
            background: var(--ghost-white, #f5f7ff);
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 14px;
          }

          .attendee-toggle-icon {
            color: var(--blue-lefebvre);
            font-size: 16px;
            width: 16px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .attendee-chip-measurements {
            position: absolute;
            visibility: hidden;
            pointer-events: none;
            white-space: nowrap;
            height: 0;
            overflow: hidden;
          }


          .header-address-container > input {
            width: 100%;
            min-width: 180px;
            border: none;
            padding: 2px 4px;
            border-radius: 4px;
            height: 24px;
            font-family: Arial, sans-serif;
            font-size: 12px;
            color: var(--grey-800);
            background: transparent;
          }

          .header-address-container > input:focus {
            outline: none;
          }

          .header-address-suggestions-list {
            text-align: left;
            padding: 0px;
            max-height: 300px;
            overflow-y: auto;
            box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1),
                        4px 0px 6px rgba(0, 0, 0, 0.1),
                        -4px 0px 6px rgba(0, 0, 0, 0.1);
            border-radius: 5px;
          }

          .header-address-suggestion {
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 0 20px 0 10px;
            height: 40px;
            box-sizing: border-box;
          }

          .header-address-suggestion:hover {
            background-color: #F2F3F8;
          }

          .header-address-suggestion-highlighted {
            background-color: rgba(0, 0, 0, 0.1);
          }

          .lexon-badge {
            background-color: #E5E8F1;
            color: black;
            font-size: 12px;
            padding: 0 8px;
            border-radius: 4px;
            margin-left: 8px;
            font-weight: 400;
            flex-shrink: 0;
            font-family: Inter, Arial, sans-serif;
            height: 22px;
            display: inline-flex;
            align-items: center;
            box-sizing: border-box;
          }

          .suggestion-content {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .suggestion-text {
          }

          .mdc-chip {
            border: 1px solid #99A3C9;
            border-radius: 20px;
            color: rgba(0, 0, 0, 0.87);
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 16px;
            font-weight: 400;
            display: inline-flex;
            position: relative;
            align-items: center;
            box-sizing: border-box;
            outline: none;
            cursor: pointer;
            overflow: hidden;
            height: 22px;
            max-width: 280px;
            padding: 0 6px;
            margin: 0 5px 0 0 !important;
          }

          .mdc-chip:hover {
            background-color: rgba(0, 0, 0, 0.1);
          }

          .mdc-chip--measure {
            pointer-events: none;
          }

          .mdc-chip__text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 220px;
            font-family: Arial, sans-serif;
            font-size: 12px;
            font-weight: 400;
            line-height: 16px;
            color: var(--grey-800);
            margin-right: 5px;
          }

          .mdc-chip-icon {
            color: var(--grey-600);
            font-size: 12px;
            cursor: pointer;
            flex-shrink: 0;
          }

          @media (max-width: 992px) {
            .mdc-chip {
              max-width: 200px;
            }

            .mdc-chip__text {
              max-width: 150px;
            }
          }
        `}</style>
      </div>
    )
    if (portalContainer) {
      return ReactDOM.createPortal(content, portalContainer);
    }
    return content;
  }

  renderSuggestion = (suggestionValue) => {
    const isLexonContact = this.contactSourceMap && this.contactSourceMap.get(suggestionValue);

    return (
      <div className="suggestion-content">
        <span className="suggestion-text">{suggestionValue}</span>
        {isLexonContact && <span className="lexon-badge">LexON</span>}
      </div>
    );
  };

  fieldClick() {
    if (this.inputRef.current && this.inputRef.current.input) {
      this.inputRef.current.input.focus();
    }
  }
 
  static clearValidation(target) {
    target.setCustomValidity('');
  }

  onSuggestionChange(event, {newValue}) {
    this.setState({value: newValue});
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async onSuggestionsFetchRequested({ value }) {
    const { userAccount, lexon } = this.props;
    this.callIndex += 1;
    const callCurrent = this.callIndex;
    await this.sleep(800);
    if (callCurrent === this.callIndex && callCurrent !== -1 && value !== '' && this._isMounted) {
      try {
        const [contacts, contactsLexon] = await Promise.all([
          getContactFilterList(userAccount, lexon.account, lexon.lexonToken, value, 'contacts'),
          this.getDataLexon(value)
        ]);
        if (this._isMounted && callCurrent === this.callIndex) {
          const prioritizedSuggestions = this.prioritizeSuggestions(contacts, contactsLexon, value);

          this.setState({
            suggestions: prioritizedSuggestions
          });
        }
      } catch (error) {
        console.error('Primary contact fetch failed, trying fallback:', error);
        try {
          const [contacts, contactsLexon] = await Promise.all([
            getContactFilterList(userAccount, lexon.account, lexon.lexonToken, value, 'contactos'),
            this.getDataLexon(value)
          ]);
          if (this._isMounted && callCurrent === this.callIndex) {
            const prioritizedSuggestions = this.prioritizeSuggestions(contacts, contactsLexon, value);

            this.setState({
              suggestions: prioritizedSuggestions
            });
          }
        } catch (fallbackError) {
          console.error('Fallback contact fetch also failed:', fallbackError);
          try {
            const contactsLexon = await this.getDataLexon(value);
            if (this._isMounted && callCurrent === this.callIndex) {
              const prioritizedSuggestions = this.prioritizeSuggestions([], contactsLexon, value);
              this.setState({
                suggestions: prioritizedSuggestions
              });
            }
          } catch (lexonError) {
            console.error('Even Lexon contacts failed:', lexonError);
            if (this._isMounted && callCurrent === this.callIndex) {
              this.setState({ suggestions: [] });
            }
          }
        }
      }
    }
  }

  onSuggestionsClearRequested() {
    if (this._isMounted) {
      this.setState({suggestions: []});
      this.contactSourceMap.clear();
    }
  }

  onSuggestionSelected(event, { suggestionValue }) {
    if (this._isMounted) {
      this.setState({ value: "", suggestions: [] });
    }
    const cleanSuggestionValue = suggestionValue.replace(/\s*LexOn\s*$/, '');

    const match = cleanSuggestionValue.match(/(.*)<(.*)>/);
    let name = '';
    let email = '';

    if (match) {
      name = match[1].replace(/"/g, '').trim();
      email = match[2].trim();
    } else {
      email = this.extractEmailFromSuggestion(suggestionValue);
    }

    const normalizedEmail = (email || '').trim();
    if (!normalizedEmail) {
      return;
    }

    const emailExists = this.props.addresses.includes(normalizedEmail);

    if (name && this._isMounted) {
      this.setState(prevState => ({
        nameEmailMap: {
          ...prevState.nameEmailMap,
          [normalizedEmail]: name
        }
      }));
    }

    if (!emailExists) {
      this.props.onAddressAdd(this.props.id, normalizedEmail);
    }
  }

  onHeaderKeyDown(event) {
    const target = event.target;
    AttendeeAddress.clearValidation(target);

    if ((event.key === "Backspace" || event.key === "Delete") && target.value.trim().length === 0) {
      const { addresses } = this.props;

      if (addresses.length > 0) {
        const addressToRemove = addresses[addresses.length - 1];
        this.handleAddressRemove(target.id, addressToRemove);
        event.preventDefault();
      }

      return;
    }

    if (event.key === "Enter" || event.key === ";") {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const existsInvalid = !emailRegex.test(target.value);
        this.props.setInvalidEmail(existsInvalid);
        const id = target.id;
        const value = target.value.replace(/;/g, "");
        this.props.onAddressAdd(id, value);
        this.setState({ value: "" });
        target.focus();
        event.preventDefault();
    }
  }
  
  onHeaderBlur(event) {
    const target = event.target;
    if (target.value.length > 0) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const existsInvalid = !emailRegex.test(target.value);
        this.props.setInvalidEmail(existsInvalid);
        this.props.onAddressAdd(target.id, target.value);
        this.setState({ value: "" });
    }
  }

  static onAddressDragStart(event, id, address) {
    event.stopPropagation();
    const payload = {id, address};
    event.dataTransfer.setData('application/json', JSON.stringify(payload));
  }

  onDrop(event, id) {
    event.preventDefault();
    const types = event.dataTransfer.types;
    if (types && Array.from(types).indexOf('application/json') >= 0) {
      try {
        const payload = JSON.parse(event.dataTransfer.getData('application/json'));
        if (id && id !== payload.id) {
          const fromId = payload.id;
          const address = payload.address;
          this.props.onAddressMove(fromId, id, address);
        }
      } catch (error) {
        console.log('Error parsing drag and drop data:', error);
      }
    }
  }

  static getSuggestionValue(suggestion) {
    return suggestion.replace(/\s*LexOn\s*$/, '');
  }
}

AttendeeAddress.propTypes = {
  t: PropTypes.func,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  chipClassName: PropTypes.string,
  autoSuggestClassName: PropTypes.string,
  autoSuggestMenuClassName: PropTypes.string,
  addresses: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string,
  getAddresses: PropTypes.func,
  onAddressAdd: PropTypes.func,
  onAddressRemove: PropTypes.func,
  onAddressMove: PropTypes.func,
  userAccount: PropTypes.string,
  userPw: PropTypes.string,
  lexon: PropTypes.shape({
    userId: PropTypes.string,
    idClientLef: PropTypes.number
  })
};

AttendeeAddress.defaultProps = {
  className: '',
  chipClassName: '',
  autoSuggestClassName: '',
  autoSuggestMenuClassName: '',
  addresses: [],
  label: '',
  lexon: {}
};

export default React.forwardRef((props, ref) => (
  <AttendeeAddress {...props} forwardedRef={ref} />
));
